/**
 * Instanced sphere and cylinder impostor batches plus the material set for every pass variant.
 * Geometry sources: SphereInstances / CylinderInstances from atomistic.ts (Å, domain frame).
 */
import {
  AlwaysStencilFunc, BackSide, DoubleSide, InstancedBufferAttribute, InstancedBufferGeometry, KeepStencilOp, LessEqualDepth, Mesh,
  NotEqualStencilFunc, RawShaderMaterial, ReplaceStencilOp, Vector3, ZeroStencilOp, type IUniform, type Side,
} from 'three';
import { INTERACTION } from '@nanome-lite/look';
import type { CylinderInstances, SphereInstances } from '../atomistic';
import { cylinderBoxGeometry, makeMaterial, sphereQuadGeometry, type SharedUniforms } from '../gl';
import { encodePickId, pickIdToBytes } from '../pick-id';
import { CYLINDER_FRAG, CYLINDER_VERT, SPHERE_FRAG, SPHERE_VERT } from '../shaders';

export type ImpostorPass = 'main' | 'pick' | 'mask' | 'band' | 'shell' | 'shadow';

/** Render order slots (Nanome queues: surface Geometry+501, mask -1, band +2, shell +3). */
export const RENDER_ORDER = {
  skybox: -1000, opaqueMesh: 10, impostor: 20, mask: 21, transparentBack: 30, transparentFront: 31, band: 40, shell: 41, hull: 42, label: 50,
} as const;

function pickBytes(count: number, slot: number, index: ArrayLike<number>): Uint8Array {
  const out = new Uint8Array(count * 4);
  if (slot > 0) for (let i = 0; i < count; i++) pickIdToBytes(encodePickId(slot, index[i]!), out, i * 4);
  return out;
}

export class SphereBatch {
  readonly geometry: InstancedBufferGeometry;
  readonly count: number;
  constructor(s: SphereInstances, pickSlot: number, ratio?: Float32Array) {
    this.count = s.count;
    const g = sphereQuadGeometry();
    g.instanceCount = s.count;
    g.setAttribute('aCenter', new InstancedBufferAttribute(s.center, 3));
    g.setAttribute('aRadius', new InstancedBufferAttribute(s.radius, 1));
    g.setAttribute('aColor', new InstancedBufferAttribute(s.color, 3));
    g.setAttribute('aPick', new InstancedBufferAttribute(pickBytes(s.count, pickSlot, s.atomIndex), 4, true));
    g.setAttribute('aRatio', new InstancedBufferAttribute(ratio ?? new Float32Array(s.count).fill(1), 1));
    this.geometry = g;
  }
  dispose(): void { this.geometry.dispose(); }
}

export class CylinderBatch {
  readonly geometry: InstancedBufferGeometry;
  readonly count: number;
  constructor(c: CylinderInstances, pickSlot: number, ratio?: Float32Array) {
    this.count = c.count;
    const g = cylinderBoxGeometry();
    g.instanceCount = c.count;
    g.setAttribute('aStart', new InstancedBufferAttribute(c.start, 3));
    g.setAttribute('aEnd', new InstancedBufferAttribute(c.end, 3));
    g.setAttribute('aRadius', new InstancedBufferAttribute(c.radius, 1));
    g.setAttribute('aColorStart', new InstancedBufferAttribute(c.colorStart, 3));
    g.setAttribute('aColorEnd', new InstancedBufferAttribute(c.colorEnd, 3));
    g.setAttribute('aFlags', new InstancedBufferAttribute(Float32Array.from(c.flags), 1));
    g.setAttribute('aPick', new InstancedBufferAttribute(pickBytes(c.count, pickSlot, c.bondIndex), 4, true));
    g.setAttribute('aRatio', new InstancedBufferAttribute(ratio ?? new Float32Array(c.count).fill(1), 1));
    this.geometry = g;
  }
  dispose(): void { this.geometry.dispose(); }
}

/** Materials per pass; shell materials are created per colour. */
export class ImpostorMaterials {
  readonly sphere: Record<Exclude<ImpostorPass, 'shell'>, RawShaderMaterial>;
  readonly cylinder: Record<Exclude<ImpostorPass, 'shell'>, RawShaderMaterial>;
  private readonly shells = new Map<string, { sphere: RawShaderMaterial; cylinder: RawShaderMaterial }>();
  private readonly all: RawShaderMaterial[] = [];

  constructor(private readonly shared: SharedUniforms) {
    const mk = (vert: string, frag: string, pass: Exclude<ImpostorPass, 'shell'>, caps: boolean, side: Side = DoubleSide) => {
      // Shadow casters stay binary and unfaded (applyFade = false, ImpostorCylinder.hlsl:115-120).
      const defines: Record<string, number> = pass === 'shadow' ? { SHADOW: 1 } : { FADE: 1 };
      if (caps) defines.CAPS = 1;
      if (pass === 'pick') defines.PICK = 1;
      if (pass === 'mask') defines.MASK = 1;
      if (pass === 'band') defines.BAND = 1;
      const m = makeMaterial(vert, frag, shared, {
        defines, side, uniforms: { uShellColor: { value: new Vector3(1, 1, 1) } },
        blend: pass === 'main', depthWrite: pass === 'main' || pass === 'pick' || pass === 'shadow', colorWrite: pass === 'main' || pass === 'pick',
      });
      m.depthFunc = LessEqualDepth;
      if (pass === 'mask') {
        // Near surface written to stencil (ImpostorHighlightPasses.cs:114-123). Depth-tested against the drawn surface.
        m.stencilWrite = true; m.stencilFunc = AlwaysStencilFunc; m.stencilRef = 1; m.stencilZPass = ReplaceStencilOp;
        m.stencilFail = KeepStencilOp; m.stencilZFail = KeepStencilOp;
      } else if (pass === 'band') {
        // Far shell pushed back by the band clears stencil where it passes depth (:125-133).
        m.stencilWrite = true; m.stencilFunc = AlwaysStencilFunc; m.stencilRef = 0; m.stencilZPass = ZeroStencilOp;
        m.stencilFail = KeepStencilOp; m.stencilZFail = KeepStencilOp;
      }
      this.all.push(m);
      return m;
    };
    // Sphere quads: Cull Off; cylinder boxes: Cull Back (SphereImpostorWebGL.shader:93, CylinderImpostorWebGL.shader:103).
    // Shadow casters: sphere quads Cull Off (SphereImpostorWebGL.shader:128-134); cylinder boxes Cull Front so the far
    // faces supply raster depth (CylinderImpostorWebGL.shader:139-145).
    this.sphere = { main: mk(SPHERE_VERT, SPHERE_FRAG, 'main', false), pick: mk(SPHERE_VERT, SPHERE_FRAG, 'pick', false), mask: mk(SPHERE_VERT, SPHERE_FRAG, 'mask', false), band: mk(SPHERE_VERT, SPHERE_FRAG, 'band', false), shadow: mk(SPHERE_VERT, SPHERE_FRAG, 'shadow', false) };
    this.cylinder = { main: mk(CYLINDER_VERT, CYLINDER_FRAG, 'main', true, DoubleSide), pick: mk(CYLINDER_VERT, CYLINDER_FRAG, 'pick', true, DoubleSide), mask: mk(CYLINDER_VERT, CYLINDER_FRAG, 'mask', true, DoubleSide), band: mk(CYLINDER_VERT, CYLINDER_FRAG, 'band', true, DoubleSide), shadow: mk(CYLINDER_VERT, CYLINDER_FRAG, 'shadow', true, BackSide) };
  }

  /** Far-surface unlit shell drawn where the mask wrote no stencil (ConfigureShell: Comp NotEqual). */
  shell(colorHex: number): { sphere: RawShaderMaterial; cylinder: RawShaderMaterial } {
    const key = colorHex.toString(16);
    let s = this.shells.get(key);
    if (s) return s;
    const c = new Vector3(((colorHex >> 16) & 255) / 255, ((colorHex >> 8) & 255) / 255, (colorHex & 255) / 255);
    const uShellColor: IUniform<Vector3> = { value: c };
    const mk = (vert: string, frag: string, caps: boolean) => {
      const defines: Record<string, number> = { FADE: 1, SHELL: 1 };
      if (caps) defines.CAPS = 1;
      const m = makeMaterial(vert, frag, this.shared, { defines, side: DoubleSide, uniforms: { uShellColor }, blend: true, depthWrite: true });
      m.depthFunc = LessEqualDepth;
      m.stencilWrite = true; m.stencilWriteMask = 0; m.stencilFunc = NotEqualStencilFunc; m.stencilRef = 1;
      m.stencilFail = KeepStencilOp; m.stencilZFail = KeepStencilOp; m.stencilZPass = KeepStencilOp;
      this.all.push(m);
      return m;
    };
    s = { sphere: mk(SPHERE_VERT, SPHERE_FRAG, false), cylinder: mk(CYLINDER_VERT, CYLINDER_FRAG, true) };
    this.shells.set(key, s);
    return s;
  }

  dispose(): void { for (const m of this.all) m.dispose(); this.shells.clear(); }
}

export const HOVER_COLOR = INTERACTION.hoverColor;
export const SELECTION_COLOR = INTERACTION.selectionColor;

/** Build a Mesh for a batch and pass with no frustum culling and an externally managed matrix. */
export function impostorMesh(geometry: InstancedBufferGeometry, material: RawShaderMaterial, renderOrder: number): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.matrixAutoUpdate = false;
  mesh.renderOrder = renderOrder;
  return mesh;
}
